/**
 * Error Classification Helper
 *
 * Classifies errors from API operations into specific types
 * Extracts backend-specific error messages for accurate user feedback
 * Provides structured error details for consistent error handling
 */

import { QuoteErrorType, QuoteErrorDetail } from "./error-types";

interface ApiError extends Error {
  userMessage?: string;
  statusCode?: number;
  backendCode?: string;
  details?: Record<string, unknown>;
}

/**
 * Classify an error and return structured error details
 *
 * Priority:
 * 1. Backend userMessage (최우선 - 백엔드의 사용자 친화적 메시지)
 * 2. API error message (백엔드의 구체적인 에러 메시지)
 * 3. Error type classification (fallback)
 *
 * @param error - The error to classify
 * @param fallbackType - Fallback error type if classification fails
 * @returns Structured error detail
 */
export function classifyQuoteError(
  error: unknown,
  fallbackType: QuoteErrorType = QuoteErrorType.UNKNOWN,
): QuoteErrorDetail {
  const apiError = error as ApiError;

  // Priority 1: Use backend's userMessage if available
  if (apiError?.userMessage) {
    const message = apiError.userMessage;
    const statusCode = apiError.statusCode;

    // Determine error type based on status code and message
    let type = fallbackType;
    let action: string | undefined;

    if (
      statusCode === 400 ||
      message.toLowerCase().includes("validation") ||
      message.toLowerCase().includes("유효성")
    ) {
      type = QuoteErrorType.VALIDATION_ERROR;
      action = "위의 오류를 확인하고 다시 시도해주세요.";
    } else if (
      message.toLowerCase().includes("file") ||
      message.toLowerCase().includes("upload") ||
      message.toLowerCase().includes("파일")
    ) {
      type = QuoteErrorType.UPLOAD_FAILED;
      action = "파일을 다시 선택하고 시도해주세요.";
    } else if (statusCode && statusCode >= 500) {
      type = QuoteErrorType.UNKNOWN;
      action = "서버 오류입니다. 잠시 후 다시 시도해주세요.";
    } else if (statusCode === 401 || statusCode === 403) {
      type = QuoteErrorType.UNKNOWN;
      action = "권한이 없습니다. 다시 로그인해주세요.";
    }

    return {
      type,
      message: message, // 백엔드의 구체적인 메시지 그대로 사용
      action,
      technicalDetail: apiError.message,
    };
  }

  // Priority 2: Try to extract message from error object
  if (error instanceof Error) {
    const message = error.message;

    // Network errors
    if (
      message.toLowerCase().includes("network") ||
      message.toLowerCase().includes("fetch")
    ) {
      return {
        type: QuoteErrorType.NETWORK_ERROR,
        message: "네트워크 오류",
        action: "인터넷 연결을 확인하고 다시 시도해주세요.",
        technicalDetail: message,
      };
    }

    // Validation errors (HTTP 400)
    if (
      message.toLowerCase().includes("400") ||
      message.toLowerCase().includes("validation")
    ) {
      return {
        type: QuoteErrorType.VALIDATION_ERROR,
        message: message, // Use specific message if available
        action: "위의 오류를 확인하고 다시 시도해주세요.",
        technicalDetail: message,
      };
    }

    // Upload-specific errors
    if (
      message.toLowerCase().includes("file") ||
      message.toLowerCase().includes("upload")
    ) {
      return {
        type: QuoteErrorType.UPLOAD_FAILED,
        message: "파일 업로드 실패",
        action: "파일을 다시 선택하고 시도해주세요.",
        technicalDetail: message,
      };
    }
  }

  // Fallback to provided type
  const message = error instanceof Error ? error.message : String(error);
  return {
    type: fallbackType,
    message:
      fallbackType === QuoteErrorType.UPLOAD_FAILED
        ? "파일 업로드 실패"
        : "견적 제출 실패",
    action:
      fallbackType === QuoteErrorType.UPLOAD_FAILED
        ? "파일을 다시 선택하고 시도해주세요."
        : "입력 정보를 확인하고 다시 시도해주세요.",
    technicalDetail: message,
  };
}
